import React, { useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { m } from "framer-motion";
import { Parallax } from "react-scroll-parallax";
import { ToastContainer, toast } from "react-toastify";
import { Loader2, Search, Send } from "lucide-react";
import SideButtons from "../../Components/SideButtons";
import { fadeIn } from "../../Functions/GlobalAnimations";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const HelpCenter = () => {
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isRtl = i18n.language === "fr";
  const direction = isRtl ? "rtl" : "ltr";

  const allFaqs = [
    {
      id: 1,
      category: 'Getting Started',
      question: 'Assistance Technique : N\'hésitez pas à nous contacter',
      answer: 'En cas de difficulté ou de problème technique, n\'hésitez pas à contacter notre support technique. Notre équipe est là pour vous aider à résoudre rapidement toute anomalie ou question que vous pourriez rencontrer. Vous pouvez nous envoyer un message via le formulaire de contact ou utiliser le bouton d’assistance disponible sur la plateforme. Votre satisfaction est notre priorité !',
    },
  ];

  const filteredFaqs = allFaqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error(t("help.form.required_fields"));
      return;
    }

    setIsSubmitting(true);

    // Simulation d'envoi (remplace par ton vrai endpoint)
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      toast.success(t("help.form.success"));
      setContactForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(t("help.form.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden" style={{ direction }}>
      <SideButtons />

      {/* Fond décoratif subtil */}
      <div className="fixed inset-0 pointer-events-none opacity-10">
        <div
          className="absolute top-20 right-20 w-80 h-80 rounded-full blur-3xl"
          style={{ backgroundColor: "#f29f2f" }}
        />
        <div
          className="absolute bottom-20 left-20 w-96 h-96 rounded-full blur-3xl"
          style={{ backgroundColor: "#0879bd" }}
        />
      </div>

      <m.div className="h-[380px] overflow-hidden sm:h-[365px] relative" {...fadeIn}>
              <Parallax
                className="lg-no-parallax bg-cover absolute top-0 left-0 w-full h-full"
                translateY={[-40, 40]}
                style={{ backgroundImage: `url(/assets/img/webp/portfolio-bg.webp)` }}
              />
              <Container className="h-full relative">
                <Row className="items-stretch justify-center h-[450px] md:h-[375px] sm:h-[350px]">
                  <Col xl={6} lg={7} md={8} xs={12} className="text-center flex justify-center flex-col">
                    <h2 className="text-blue-900 mb-[0px] font-serif font-medium -tracking-[1px] my-auto xs:mb-[0px]">
               {"Centre d'Aide"}
              </h2>
            </Col>
          </Row>
        </Container>
      </m.div>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 min-md:py-16">
        <div className="bg-white rounded-3xl shadow-2xl p-8 min-md:p-12">
          {/* Barre de recherche stylée */}
          <div className="mb-12">
            <form onSubmit={(e) => { e.preventDefault(); }}>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Recherchez une réponse (ex : Comment acheter un document ?)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-5 py-4 border-2 rounded-2xl focus:outline-none transition-all text-lg"
                  style={{
                    borderColor: "#e5e7eb",
                    boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#0879bd";
                    e.target.style.boxShadow = "0 0 0 4px rgba(8,121,189,0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e5e7eb";
                    e.target.style.boxShadow = "0 4px 15px rgba(0,0,0,0.05)";
                  }}
                />
                <Search
                  size={24}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                />
              </div>
            </form>
          </div>

          {/* FAQ */}
          <div className="mb-16">
            <h2 className="text-xxxl font-bold mb-8 text-center min-md:text-left" style={{ color: "#003479" }}>
              Assistance Technique
            </h2>

            {filteredFaqs.length > 0 ? (
              <div className="space-y-8">
                {filteredFaqs.map((faq) => (
                  <div
                    key={faq.id}
                    className="bg-gray-50 rounded-2xl p-6 min-md:p-8 hover:shadow-lg transition-all duration-300"
                  >
                    <h3 className="text-xxl font-bold mb-3" style={{ color: "#003479" }}>
                      {faq.question}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-2xl">
                <p className="text-xl text-gray-600">
                  {"Aucun résultat trouvé pour"} "{searchQuery}".
                </p>
                <p className="text-gray-500 mt-2">
                  {"Essayez une autre recherche."}
                </p>
              </div>
            )}
          </div>

          {/* Contact */}
          <div>
            <h2 className="text-xxxl font-bold mb-8 text-center min-md:text-left" style={{ color: "#003479" }}>
              {"Contactez-nous"}
            </h2>
            <p className="text-gray-700 text-lg mb-8 text-center min-md:text-left">
              {"Besoin d'aide ? Contactez notre équipe par email à"}{" "}
              <a
                href="mailto:support@jaafaracademy.com"
                className="font-bold text-[#0879bd] hover:underline"
              >
                support@jaafaracademy.com
              </a>{" "}
            </p>

            <form onSubmit={handleContactSubmit} className="space-y-6 max-w-3xl mx-auto">
              <div className="grid min-md:grid-cols-2 gap-6">
                <input
                  type="text"
                  placeholder="Votre nom"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  required
                  className="w-full px-5 py-4 border-2 rounded-xl focus:outline-none transition-all text-lg"
                  style={{ borderColor: "#e5e7eb" }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#0879bd";
                    e.target.style.boxShadow = "0 0 0 4px rgba(8,121,189,0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e5e7eb";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <input
                  type="email"
                  placeholder={"Votre email"}
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  required
                  className="w-full px-5 py-4 border-2 rounded-xl focus:outline-none transition-all text-lg"
                  style={{ borderColor: "#e5e7eb" }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#0879bd";
                    e.target.style.boxShadow = "0 0 0 4px rgba(8,121,189,0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e5e7eb";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
              <textarea
                placeholder={"Décrivez votre problème"}
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                required
                rows="6"
                className="w-full px-5 py-4 border-2 rounded-xl focus:outline-none transition-all resize-none text-lg"
                style={{ borderColor: "#e5e7eb" }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#0879bd";
                  e.target.style.boxShadow = "0 0 0 4px rgba(8,121,189,0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 rounded-xl font-bold text-lg text-white transition-all duration-300 flex items-center justify-center gap-3 ${
                  isSubmitting ? "opacity-70 cursor-not-allowed" : "hover:shadow-xl hover:scale-[1.02]"
                }`}
                style={{ backgroundColor: "#0879bd" }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    {"Envoi en cours..."}
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    {"Envoyer"}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </main>

      <ToastContainer position={isRtl ? "bottom-right" : "bottom-left"} autoClose={3000} />
    </div>
  );
};

export default HelpCenter;